import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import LogoSwap from "../components/LogoSwap";
import LanguageToggle from "../components/LanguageToggle";
import { useT } from "../lib/useLanguage";

const SKILL_CHIPS = ["Python", "CV", "Running", "English", "Excel", "Design", "Guitar", "Maths", "Canva", "Cuisine"];

const HOW_STEPS_FR = [
  { icon: "✦", title: "Choisis un skill ou une activité", desc: "Langues, code, sport, créatif, vie pratique..." },
  { icon: "◎", title: "Découvre des profils et sessions", desc: "Quelqu'un offre, quelqu'un cherche — autour de toi." },
  { icon: "✉", title: "Envoie une demande structurée", desc: "Un message unique. Pas de spam." },
  { icon: "⌖", title: "Échange en vrai ou en ligne", desc: "Campus, café, bibliothèque ou visio." },
];

const HOW_STEPS_EN = [
  { icon: "✦", title: "Choose a skill or activity", desc: "Languages, code, sport, creative, everyday life..." },
  { icon: "◎", title: "Discover profiles and sessions", desc: "Someone offers, someone needs — nearby." },
  { icon: "✉", title: "Send a structured request", desc: "One message. No spam." },
  { icon: "⌖", title: "Meet online or in person", desc: "Campus, café, library or video call." },
];

const EXAMPLES = ["Français ↔ Anglais", "Maths ↔ Guitare", "CV ↔ Design", "Excel ↔ Cuisine", "Python ↔ Espagnol", "Running ↔ Admin"];

export default function Landing() {
  const t = useT();
  const lang = localStorage.getItem("swap_lang") || "fr";
  const steps = lang === "en" ? HOW_STEPS_EN : HOW_STEPS_FR;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFF4E8" }}>
      {/* Nav */}
      <div className="flex items-center justify-between px-5 py-4 max-w-lg mx-auto">
        <LogoSwap size="sm" />
        <LanguageToggle />
      </div>

      {/* Hero */}
      <section className="relative px-5 pt-10 pb-20 max-w-lg mx-auto overflow-hidden">
        {/* Background shapes */}
        <div className="absolute -top-10 -right-16 w-48 h-48 rounded-full opacity-25 pointer-events-none" style={{ backgroundColor: "#B9F85A" }} />
        <div className="absolute top-20 -left-10 w-32 h-32 rounded-full opacity-20 pointer-events-none" style={{ backgroundColor: "#A7D8FF" }} />
        <div className="absolute bottom-0 right-8 w-24 h-24 rounded-full opacity-15 pointer-events-none" style={{ backgroundColor: "#5B4BFF" }} />

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: "easeOut" }}>
          <h1 className="text-4xl sm:text-5xl font-black leading-tight mb-3" style={{ color: "#101828" }}>
            {lang === "en" ? "Swap skills\nin Talence." : "Swap tes skills\nà Talence."}
          </h1>
          <p className="text-lg font-medium mb-8" style={{ color: "#101828", opacity: 0.55 }}>
            {lang === "en" ? "Find a hand. Offer yours." : "Trouve un coup de main. Propose le tien."}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-10">
            <Button asChild size="lg" className="rounded-full text-base px-8 font-extrabold shadow-lg h-14"
              style={{ backgroundColor: "#FF6B4A", color: "#fff" }}>
              <Link to="/discover">
                {lang === "en" ? "Open Swap" : "Ouvrir Swap"} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full text-base px-8 font-semibold border-2 h-14"
              style={{ borderColor: "#101828", color: "#101828", backgroundColor: "transparent" }}>
              <Link to="/profile/edit">
                {lang === "en" ? "Offer a skill" : "Proposer un skill"}
              </Link>
            </Button>
          </div>

          {/* Floating skill chips */}
          <div className="flex flex-wrap gap-2">
            {SKILL_CHIPS.map((s, i) => (
              <motion.span
                key={s}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.04, duration: 0.3 }}
                className="px-3.5 py-2 rounded-full text-sm font-semibold border"
                style={{
                  backgroundColor: i % 3 === 0 ? "#B9F85A" : i % 3 === 1 ? "#fff" : "#FF6B4A15",
                  color: "#101828",
                  borderColor: i % 3 === 0 ? "#B9F85A" : i % 3 === 1 ? "#e5e7eb" : "transparent",
                }}>
                {s}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How it works */}
      <section className="px-5 py-16 bg-white">
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl font-extrabold mb-8" style={{ color: "#101828" }}>
            {lang === "en" ? "How it works" : "Comment ça marche"}
          </h2>
          <div className="space-y-5">
            {steps.map((step, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="flex gap-4 items-start">
                <div className="h-10 w-10 rounded-2xl flex items-center justify-center text-base font-bold shrink-0"
                  style={{ backgroundColor: ["#B9F85A", "#A7D8FF30", "#FF6B4A20", "#5B4BFF15"][i], color: "#101828" }}>
                  {step.icon}
                </div>
                <div>
                  <h3 className="font-bold text-foreground">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="px-5 py-16" style={{ backgroundColor: "#FFF4E8" }}>
        <div className="max-w-lg mx-auto">
          <h2 className="text-2xl font-extrabold mb-6" style={{ color: "#101828" }}>
            {lang === "en" ? "Exchange examples" : "Exemples d'échanges"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((e, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: i % 2 === 0 ? "#101828" : "#FF6B4A" }}>
                {e}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-5 py-16 text-center" style={{ backgroundColor: "#101828" }}>
        <div className="max-w-lg mx-auto">
          <p className="text-white/50 text-sm mb-3">Talence · Campus · Peixotto · Forum · En ligne</p>
          <h2 className="text-3xl font-extrabold text-white mb-6">
            {lang === "en" ? "Ready to swap?" : "Prêt·e à swapper ?"}
          </h2>
          <Button asChild size="lg" className="rounded-full px-10 font-extrabold h-14" style={{ backgroundColor: "#B9F85A", color: "#101828" }}>
            <Link to="/discover">
              {lang === "en" ? "Open Swap" : "Ouvrir Swap"} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}